import { ChevronRightIcon } from '@langchain/untitled-ui-icons';
import Link from '@mui/joy/Link';
import Typography from '@mui/joy/Typography';

import {
  cloneElement,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link as ReactRouterDOMLink,
  Link as RouterLink,
  useLocation,
  useMatches,
} from 'react-router-dom';

import { Tooltip } from '@/design-system';
import { useCondensedCrumbs } from '@/hooks/useCondensedCrumbs';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { useSetPageTitle } from '@/hooks/useSetPageTitle';
import { useOrganizationId } from '@/hooks/useSwr';
import StarIcon from '@/icons/StarIcon.svg?react';
import { appOrganizationPath } from '@/utils/constants';
import { isPublicMatch } from '@/utils/is-public-match';
import { cn } from '@/utils/tailwind';

import { BreadcrumbProps } from './Breadcrumbs.types';
import { ResourceTagFilter } from './ResourceTagFilter';

export interface BreadcrumbData {
  name: string;
  icon?: React.ReactNode;
  path?: string;
  isActive?: boolean;
  isFirst?: boolean;
  condensed?: boolean;
}

export const Breadcrumb = forwardRef<HTMLAnchorElement, BreadcrumbData>(
  (props, ref) => {
    useSetPageTitle({
      isActive: !!props.isActive,
      name: props.name,
    });

    if (!props.path) {
      return null;
    }

    return (
      <Link
        component={RouterLink}
        to={props.path}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          flexShrink: 1,
          cursor: props.path === '#' ? 'default' : undefined,
          pointerEvents: props.path === '#' ? 'none' : undefined,
          textDecoration: 'none',
          color: props.isActive
            ? theme.palette.text.primary
            : theme.palette.text.tertiary,
        })}
        ref={ref}
        fontSize="small"
        startDecorator={
          props.isFirst ? null : <ChevronRightIcon className="mx-1 size-4" />
        }
        data-testid={`breadcrumb-${props.name}`}
      >
        {props.icon}
        <Typography
          whiteSpace="pre"
          textOverflow="ellipsis"
          sx={{
            overflowX: 'clip',
            maxWidth: props.condensed ? 150 : 200,
            marginLeft: props.icon ? 0.75 : 0,
          }}
        >
          {props.name}
        </Typography>
      </Link>
    );
  }
);

const Breadcrumbs = ({
  hideResourceTagFilter = false,
}: {
  hideResourceTagFilter?: boolean;
}) => {
  const matches = useMatches();
  const location = useLocation();
  const isPaymentEnabled = useInstanceFlagOrDefault(
    InstanceFlags.payment_enabled
  ) as boolean;

  const ref = useRef<HTMLDivElement>(null);
  const crumbCount = matches.filter(
    (match) => (match.handle as any)?.crumb
  ).length;

  const condensedCrumbs = useCondensedCrumbs(ref, crumbCount);

  const { tier, isNoPlan, isLegacy, isEnterprise, isLoading } =
    useCurrentTier();
  const organizationId = useOrganizationId();
  const [urlsForMatches, setUrlsForMatches] = useState(
    {} as Record<string, string>
  );
  const leafMatch = matches
    .filter((match) => (match.handle as any)?.crumb)
    .reduce((_, match) => match, null as (typeof matches)[0] | null);
  //Don't show breadcrumb if the leaf doesn't want us to
  const hideBreadcrumb =
    leafMatch?.handle && (leafMatch.handle as any)?.hideBreadcrumb;

  const inAssignMode = matches.some(
    (m) => m.handle && (m.handle as any)?.inAssignMode
  );

  const isPublic = matches
    .slice(1)
    .every((match) => isPublicMatch(match.handle, location));

  // Store the search string for the current URL so that we can restore it when
  // the user navigates back to this page from the breadcrumbs.
  useEffect(() => {
    return () => {
      const leafMatch = matches
        .filter((match) => (match.handle as any)?.crumb)
        .reduce((_, match) => match, null as (typeof matches)[0] | null);

      setUrlsForMatches((prev) => {
        const next = {};

        // only keep keys in current matches
        const keep = new Set(matches.map((m) => m.id));
        Object.keys(prev)
          .filter((k) => keep.has(k))
          .forEach((k) => {
            next[k] = prev[k];
          });

        if (leafMatch) {
          next[leafMatch.id + '-' + leafMatch.pathname] =
            location.pathname + location.search;
        }

        return next;
      });
    };
  }, [matches, location]);

  const badgeTitle = useMemo(() => {
    if (isLegacy) {
      return 'BETA USER';
    } else if (tier === 'free') {
      return 'DEVELOPER';
    } else if (isEnterprise) {
      return 'ENTERPRISE';
    } else if (tier) {
      return tier;
    }
    return 'PENDING';
  }, [isLegacy, tier, isEnterprise]);

  const tooltipTitle = isLegacy
    ? 'LangSmith beta user – click to see settings'
    : 'Click to see settings';

  return hideBreadcrumb ? null : (
    <div
      className="flex h-[32px] flex-none items-center justify-between overflow-x-auto overflow-y-hidden"
      ref={ref}
    >
      <div className="flex h-[32px] shrink-0 grow items-center overflow-x-clip">
        {matches
          .filter((match) => (match.handle as any)?.crumb)
          .map((match, i, arr) => {
            const isLeaf = i === arr.length - 1;
            return cloneElement((match.handle as any)?.crumb, {
              key: match.id,
              path: isLeaf
                ? '#'
                : urlsForMatches[match.id + '-' + match.pathname] ??
                  match.pathname,
              isActive: i === arr.length - 1,
              isFirst: i === 0,
              condensed: condensedCrumbs,
            } satisfies BreadcrumbProps);
          })}
      </div>
      <div className="flex items-center justify-center">
        {!isPublic && !hideResourceTagFilter && (
          <ResourceTagFilter inAssignMode={inAssignMode} />
        )}
        {!isLoading && isPaymentEnabled && (
          <Tooltip title={tooltipTitle}>
            <ReactRouterDOMLink
              to={`/${appOrganizationPath}/${organizationId}/settings/payments`}
              className={cn(
                'ml-4 inline-flex cursor-pointer items-center justify-start gap-1 rounded-full bg-opacity-20 px-2 py-1',
                !isNoPlan
                  ? 'bg-brand-secondary text-brand-secondary'
                  : 'bg-tertiary text-tertiary',
                isLegacy ? 'bg-opacity-100' : 'bg-opacity-20'
              )}
            >
              {isLegacy && <StarIcon className="h-4 w-4 text-white" />}
              <div
                className={cn(
                  'text-center text-[11px] font-semibold uppercase leading-[11px] tracking-wide',
                  isLegacy && 'text-white'
                )}
              >
                {badgeTitle}
              </div>
            </ReactRouterDOMLink>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default Breadcrumbs;
